import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useSettings } from '../context/SettingsContext';
import { useCart } from '../context/CartContext';
import '../styles/payment.css';

const getBankCode = (bankName) => {
    if (!bankName) return 'MB';
    const clean = bankName.toLowerCase();
    if (clean.includes('vietcom') || clean.includes('vcb')) return 'VCB';
    if (clean.includes('mb') || clean.includes('quan doi')) return 'MB';
    if (clean.includes('techcom') || clean.includes('tcb')) return 'TCB';
    if (clean.includes('vietin') || clean.includes('ctg')) return 'CTG';
    if (clean.includes('bidv')) return 'BIDV';
    if (clean.includes('agri') || clean.includes('vba')) return 'VBA';
    if (clean.includes('acb')) return 'ACB';
    if (clean.includes('tp') || clean.includes('tpb')) return 'TPB';
    if (clean.includes('vp') || clean.includes('vpb')) return 'VPB';
    if (clean.includes('sacom') || clean.includes('stb')) return 'STB';
    if (clean.includes('shb')) return 'SHB';
    if (clean.includes('vib')) return 'VIB';
    if (clean.includes('msb')) return 'MSB';
    return bankName.toUpperCase().replace(/[^A-Z0-9]/g, '');
};

const Payment = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { settings } = useSettings();
    const { clearCart } = useCart();
    const [copiedField, setCopiedField] = useState(null);
    const [toastMessage, setToastMessage] = useState('');
    const [showToast, setShowToast] = useState(false);

    // Retrieve order data from navigation state
    const orderData = location.state?.order;

    useEffect(() => {
        // Redirect to /products if no order data is present (direct access)
        if (!orderData) {
            navigate('/products');
        }
    }, [orderData, navigate]);

    if (!orderData) return null;

    const {
        senderName,
        senderPhone,
        receiverName,
        receiverPhone,
        deliveryAddress,
        deliveryWard,
        deliveryDistrict,
        deliveryProvince,
        deliveryDate,
        deliveryTime,
        cardMessage,
        photoBeforeDelivery,
        anonymous,
        vatInvoice,
        notes,
        transferContent,
        items,
        total
    } = orderData;

    // Load bank details from settings or fallback defaults
    const bankName = settings.bank_name || 'MB Bank';
    const bankAccount = settings.bank_account || '0392621101';
    const bankOwner = settings.bank_owner || 'NGUYEN HOANG NAM';
    const bankCode = getBankCode(bankName);
    const zaloUrl = settings.zalo_url || 'https://zalo.me/0392621101';

    // VietQR image URL
    // Template 'print' displays QR + Account info + amount + message in a clean canvas
    const qrImageUrl = `https://img.vietqr.io/image/${bankCode}-${bankAccount}-print.png?amount=${total}&addInfo=${transferContent}&accountName=${encodeURIComponent(bankOwner)}`;

    const formatPrice = (price) => {
        if (price == null || price === '' || Number(price) === 0) return 'Liên hệ';
        return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(price);
    };

    const triggerToast = (msg) => {
        setToastMessage(msg);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 2500);
    };

    const handleCopy = (text, fieldName) => {
        navigator.clipboard.writeText(text)
            .then(() => {
                setCopiedField(fieldName);
                triggerToast(`Đã sao chép ${fieldName}!`);
                setTimeout(() => setCopiedField(null), 2000);
            })
            .catch(err => {
                console.error('Failed to copy text: ', err);
                triggerToast('Không thể sao chép tự động.');
            });
    };

    // Compile order text summary
    const getOrderSummaryText = () => {
        const itemLines = items.map(item => `- ${item.name} (SL: ${item.quantity}) - ${formatPrice(item.price)}`).join('\n');
        return `🌸 ĐƠN ĐẶT HOA MỚI - HOA THÁNG NĂM 🌸
-----------------------------
👤 THÔNG TIN KHÁCH HÀNG:
- Người đặt: ${senderName}
- Số điện thoại: ${senderPhone}

👤 THÔNG TIN NGƯỜI NHẬN:
- Người nhận: ${receiverName}
- Số điện thoại người nhận: ${receiverPhone}
- Địa chỉ giao: ${deliveryAddress}, ${deliveryWard}, ${deliveryDistrict}, ${deliveryProvince}
- Ngày giao: ${deliveryDate}
- Giờ giao: ${deliveryTime}

✉️ NỘI DUNG THIỆP/BANNER:
"${cardMessage || 'Không có'}"

✨ YÊU CẦU THÊM:
- Gửi ảnh trước khi giao: ${photoBeforeDelivery ? 'Có' : 'Không'}
- Giao hoa ẩn danh: ${anonymous ? 'Có' : 'Không'}
- Xuất hóa đơn VAT: ${vatInvoice ? 'Có' : 'Không'}
${notes ? `- Ghi chú thêm: ${notes}` : ''}

🛒 SẢN PHẨM ĐÃ ĐẶT:
${itemLines}

💰 TỔNG CỘNG: ${formatPrice(total)}
💳 NỘI DUNG CHUYỂN KHOẢN: ${transferContent}
🏦 NGÂN HÀNG: ${bankName} - STK: ${bankAccount} (${bankOwner})`;
    };

    const handleCompleteOrder = () => {
        // Send order to backend database
        fetch('/api/orders', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                senderName,
                senderPhone,
                receiverName,
                receiverPhone,
                deliveryAddress,
                deliveryWard,
                deliveryDistrict,
                deliveryProvince,
                deliveryDate,
                deliveryTime,
                cardMessage,
                photoBeforeDelivery,
                anonymous,
                vatInvoice,
                notes,
                transferContent,
                items,
                total
            })
        }).catch(err => {
            console.error('Lỗi khi gửi đơn hàng lên hệ thống:', err);
        });

        const summaryText = getOrderSummaryText();
        navigator.clipboard.writeText(summaryText)
            .then(() => {
                // Clear cart locally
                clearCart();
                triggerToast('Đã copy thông tin đơn hàng! Đang mở Zalo...');
                setTimeout(() => {
                    window.open(zaloUrl, '_blank');
                }, 1500);
            })
            .catch(err => {
                console.error('Copy order summary failed', err);
                clearCart();
                window.open(zaloUrl, '_blank');
            });
    };

    const downloadQRImage = async () => {
        try {
            triggerToast('Đang tải xuống mã QR...');
            const response = await fetch(qrImageUrl);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `vietqr_hoathangnam_${transferContent}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Download QR failed', error);
            // Fallback
            window.open(qrImageUrl, '_blank');
        }
    };

    return (
        <div className="payment-page-wrapper">
            <div className="payment-container">
                <div className="payment-breadcrumbs">
                    <Link to="/">Trang chủ</Link>
                    <i className="fas fa-chevron-right"></i>
                    <Link to="/dat-hang">Đặt hàng</Link>
                    <i className="fas fa-chevron-right"></i>
                    <span>Thanh toán</span>
                </div>

                <div className="payment-success-banner">
                    <i className="fas fa-check-circle success-banner-icon"></i>
                    <h1>Đã nhận thông tin đặt hoa!</h1>
                    <p>Quý khách vui lòng hoàn tất chuyển khoản dưới đây để shop chuẩn bị thiết kế hoa.</p>
                </div>

                <div className="payment-grid">
                    
                    {/* Method 1: VietQR Code */}
                    <div className="payment-card qr-card">
                        <h2 className="payment-method-title">
                            <i className="fas fa-qrcode"></i>
                            Cách 1: Quét mã QR thanh toán nhanh
                        </h2>
                        <p className="payment-method-desc">
                            Mở ứng dụng ngân hàng bất kỳ (Mobile Banking), chọn <strong>Quét mã QR</strong> và quét ảnh dưới đây. Số tiền và nội dung chuyển khoản đã được tích hợp tự động chính xác.
                        </p>
                        
                        <div className="qr-image-wrapper">
                            <img src={qrImageUrl} alt="VietQR Code" className="qr-code-image" />
                        </div>

                        <div className="qr-actions">
                            <button type="button" onClick={downloadQRImage} className="payment-btn payment-btn--outline">
                                <i className="fas fa-download"></i> Tải ảnh QR về máy
                            </button>
                        </div>
                    </div>

                    {/* Method 2: Copy manual */}
                    <div className="payment-card info-card">
                        <h2 className="payment-method-title">
                            <i className="fas fa-university"></i>
                            Cách 2: Chuyển khoản thủ công & Copy nhanh
                        </h2>
                        <p className="payment-method-desc">
                            Nếu không quét được QR, quý khách có thể chuyển khoản bằng cách sao chép các thông tin tài khoản dưới đây:
                        </p>

                        <div className="copy-fields-list">
                            
                            <div className="copy-item">
                                <div className="copy-label">Tên ngân hàng</div>
                                <div className="copy-val-row">
                                    <span className="copy-value">{bankName}</span>
                                    <button 
                                        type="button" 
                                        onClick={() => handleCopy(bankName, 'Tên ngân hàng')}
                                        className="copy-btn"
                                    >
                                        {copiedField === 'Tên ngân hàng' ? 'Đã copy ✓' : <><i className="far fa-copy"></i> Copy</>}
                                    </button>
                                </div>
                            </div>

                            <div className="copy-item">
                                <div className="copy-label">Số tài khoản</div>
                                <div className="copy-val-row">
                                    <span className="copy-value highlighted">{bankAccount}</span>
                                    <button 
                                        type="button" 
                                        onClick={() => handleCopy(bankAccount, 'Số tài khoản')}
                                        className="copy-btn copy-btn--primary"
                                    >
                                        {copiedField === 'Số tài khoản' ? 'Đã copy ✓' : <><i className="far fa-copy"></i> Copy</>}
                                    </button>
                                </div>
                            </div>

                            <div className="copy-item">
                                <div className="copy-label">Tên chủ tài khoản</div>
                                <div className="copy-val-row">
                                    <span className="copy-value uppercase">{bankOwner}</span>
                                    <button 
                                        type="button" 
                                        onClick={() => handleCopy(bankOwner, 'Tên chủ tài khoản')}
                                        className="copy-btn"
                                    >
                                        {copiedField === 'Tên chủ tài khoản' ? 'Đã copy ✓' : <><i className="far fa-copy"></i> Copy</>}
                                    </button>
                                </div>
                            </div>

                            <div className="copy-item">
                                <div className="copy-label">Số tiền cần chuyển</div>
                                <div className="copy-val-row">
                                    <span className="copy-value highlighted price">{formatPrice(total)}</span>
                                    <button 
                                        type="button" 
                                        onClick={() => handleCopy(total.toString(), 'Số tiền')}
                                        className="copy-btn copy-btn--primary"
                                    >
                                        {copiedField === 'Số tiền' ? 'Đã copy ✓' : <><i className="far fa-copy"></i> Copy</>}
                                    </button>
                                </div>
                            </div>

                            <div className="copy-item">
                                <div className="copy-label">Nội dung chuyển khoản (Bắt buộc ghi đúng)</div>
                                <div className="copy-val-row">
                                    <span className="copy-value highlighted content-code">{transferContent}</span>
                                    <button 
                                        type="button" 
                                        onClick={() => handleCopy(transferContent, 'Nội dung chuyển khoản')}
                                        className="copy-btn copy-btn--primary"
                                    >
                                        {copiedField === 'Nội dung chuyển khoản' ? 'Đã copy ✓' : <><i className="far fa-copy"></i> Copy</>}
                                    </button>
                                </div>
                            </div>

                        </div>
                    </div>

                </div>

                {/* Final step confirmation */}
                <div className="payment-final-box">
                    <h3>Bước cuối cùng: Gửi thông tin cho Shop</h3>
                    <p>
                        Sau khi thực hiện chuyển khoản thành công, quý khách vui lòng bấm nút <strong>Hoàn tất đặt hoa & Gửi Zalo</strong> dưới đây. Hệ thống sẽ sao chép toàn bộ thông tin đơn hàng này và tự động mở Zalo của shop. Quý khách chỉ cần <strong>Dán (Paste)</strong> vào khung chat để shop kiểm tra giao hoa ngay lập tức!
                    </p>
                    <button type="button" onClick={handleCompleteOrder} className="complete-order-btn">
                        <i className="fab fa-zalo"></i> Hoàn tất đặt hoa & Gửi Zalo cho Shop
                    </button>
                </div>
            </div>

            {/* Premium Toast Notice */}
            {showToast && (
                <div className="payment-toast-notice">
                    <i className="fas fa-check-circle"></i> {toastMessage}
                </div>
            )}
        </div>
    );
};

export default Payment;
